const SITEKEY = import.meta.env.VITE_RECAPTCHA_SITEKEY;

/** Plazo maximo para que el script de Google quede operativo. */
const READY_TIMEOUT_MS = 10000;

/** Intervalo del sondeo que espera a que grecaptcha se inicialice. */
const POLL_INTERVAL_MS = 50;

/** Intentos de obtener el token antes de rendirse. */
const MAX_INTENTOS = 2;

const SCRIPT_ID = 'recaptcha-v3';

// A nivel de modulo, no por componente: el script debe cargarse una sola vez.
// Se memoiza la carga, nunca el token.
let readyPromise: Promise<boolean> | null = null;

const esperarGrecaptcha = (deadline: number): Promise<boolean> => (
  new Promise<boolean>((resolve) => {
    const revisar = () => {
      // Se comprueba execute, no solo el objeto: entre que el archivo termina
      // de descargarse y que la API queda usable hay una ventana en la que
      // window.grecaptcha existe a medias.
      if (typeof window.grecaptcha?.execute === 'function') {
        resolve(true);
        return;
      }
      if (Date.now() >= deadline) {
        resolve(false);
        return;
      }
      setTimeout(revisar, POLL_INTERVAL_MS);
    };
    revisar();
  })
);

/**
 * Inyecta el script de reCAPTCHA y espera a que quede operativo.
 *
 * Resolver en el evento load no basta: dispara cuando el archivo termina de
 * descargarse, antes de que grecaptcha se haya inicializado. Por eso se sondea
 * hasta que execute esta disponible.
 */
const loadScript = (): Promise<boolean> => {
  if (readyPromise) return readyPromise;
  if (!SITEKEY) return Promise.resolve(false);

  const deadline = Date.now() + READY_TIMEOUT_MS;

  readyPromise = new Promise<boolean>((resolve) => {
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${SITEKEY}`;
      script.async = true;
      script.defer = true;
      // Un fallo de descarga corta la espera en lugar de agotar el plazo.
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    }

    esperarGrecaptcha(deadline).then(resolve);
  });

  return readyPromise;
};

/**
 * Precarga el script para que el primer envio no tenga que esperarlo.
 *
 * Se llama al arrancar la app: cargar en el momento del submit hacia que la
 * descarga y la peticion coincidieran, y el token salia nulo.
 */
export const preloadCaptcha = (): void => {
  if (SITEKEY) loadScript();
};

/**
 * Obtiene un token de reCAPTCHA v3 para una accion.
 *
 * El token es de un solo uso y dura unos dos minutos, asi que se pide uno
 * nuevo en cada llamada; aqui nunca se cachea. Se reintenta una vez porque el
 * backend rechazara las peticiones sin token, de modo que un token ausente
 * cuesta la solicitud del candidato.
 *
 * Devuelve null solo si no hay sitekey configurada o si Google es inalcanzable
 * tras los reintentos. El llamador decide que hacer con eso.
 *
 * @param action Nombre de la accion, para el scoring por accion de Google.
 * @returns El token, o null si no se pudo obtener.
 */
export const getCaptchaToken = async (action: string): Promise<string | null> => {
  if (!SITEKEY) return null;

  // Si el script no llega a estar operativo, reintentar no aporta nada: la
  // carga esta memoizada y el DOM ya tiene la etiqueta, asi que un segundo
  // intento repetiria la misma espera.
  const listo = await loadScript();
  if (!listo) return null;

  for (let intento = 1; intento <= MAX_INTENTOS; intento += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const token = await window.grecaptcha.execute(SITEKEY, { action });
      if (token) return token;
    } catch {
      // Puede ser transitorio; se reintenta mientras queden intentos.
    }
  }

  return null;
};

export default getCaptchaToken;
